<template>
  <div v-if="hasAgent" class="ai-chat-widget">
    <div
      v-show="isOpen"
      class="chat-panel"
      role="dialog"
      aria-label="Chat com IA"
    >
      <header class="chat-header">
        <div class="header-info">
          <div class="header-avatar">
            <i class="fa-solid fa-robot"></i>
          </div>
          <div>
            <h5 class="header-title">{{ agentName }}</h5>
            <span class="header-status">
              <span class="status-dot"></span>
              Online
            </span>
          </div>
        </div>

        <div class="header-actions">
          <button
            v-if="messages.length > 0"
            class="btn-header"
            title="Nova conversa"
            aria-label="Iniciar nova conversa"
            @click="startNewConversation"
          >
            <i class="fa-solid fa-rotate-right"></i>
          </button>
          <button
            class="btn-header"
            title="Fechar"
            aria-label="Fechar chat"
            @click="closeChat"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      <AIChatMessages ref="chatMessages" @send="handleSend" />

      <AIChatInput ref="chatInput" @send="handleSend" />
    </div>

    <button
      class="chat-fab"
      :class="{ open: isOpen }"
      :aria-label="isOpen ? 'Fechar chat IA' : 'Abrir chat IA'"
      @click="toggleChat"
    >
      <transition name="fab-icon" mode="out-in">
        <i v-if="!isOpen" key="chat" class="fa-solid fa-comment-dots"></i>
        <i v-else key="close" class="fa-solid fa-xmark"></i>
      </transition>
      <span v-if="!isOpen" class="fab-label">Chat IA</span>
    </button>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AIChatMessages from "./AIChatMessages.vue";
import AIChatInput from "./AIChatInput.vue";

export default {
  name: "AIChatWidget",
  components: { AIChatMessages, AIChatInput },
  computed: {
    ...mapState("agent", ["isOpen", "messages"]),
    ...mapGetters("agent", ["hasAgent", "agentName"])
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.focus();
          }
        });
      }
    }
  },
  mounted() {
    this.$store.dispatch("agent/checkAgent").catch(() => {});
  },
  beforeDestroy() {
    this.$store.dispatch("agent/cancelStream");
  },
  methods: {
    toggleChat() {
      this.$store.dispatch("agent/toggleChat");
    },

    closeChat() {
      this.$store.dispatch("agent/closeChat");
    },

    handleSend(message) {
      this.$store.dispatch("agent/sendMessage", { message });
    },

    startNewConversation() {
      this.$store.dispatch("agent/startNewConversation");
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-chat-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;
  font-family: "Noto Sans", sans-serif;

  @media screen and (max-width: 575px) {
    bottom: 16px;
    right: 16px;
  }
}

.chat-fab {
  width: auto;
  height: 48px;
  border-radius: 24px;
  border: none;
  background: linear-gradient(135deg, #ab85e5, #7c5cbf);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(171, 133, 229, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 28px rgba(171, 133, 229, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  &.open {
    width: 48px;
    padding: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }
}

.fab-label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.fab-icon-enter-active,
.fab-icon-leave-active {
  transition: all 0.2s ease;
}

.fab-icon-enter,
.fab-icon-leave-to {
  opacity: 0;
  transform: scale(0.5) rotate(90deg);
}

.chat-panel {
  position: absolute;
  bottom: 60px;
  right: 0;
  width: 380px;
  height: 520px;
  max-height: calc(100vh - 120px);
  background: #1a1a2e;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 575px) {
    width: calc(100vw - 32px);
    height: calc(100vh - 100px);
    max-height: calc(100vh - 100px);
    bottom: 56px;
    right: 0;
  }
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(171, 133, 229, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 16px;
    color: #ab85e5;
  }
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.2;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-status {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4caf50;
  display: inline-block;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.btn-header {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
  }
}
</style>
